import React from 'react';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@material-ui/icons';

/**
 * Severity level constants for technical debt items
 */
export const SEVERITY_LEVELS = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const;

/**
 * Status constants for technical debt items
 */
export const DEBT_STATUS = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  DEFERRED: 'DEFERRED',
} as const;

/**
 * Infrastructure / Mint-style debt category from dfp-api and tech-debt aggregator
 * (`debt_items[].category`, e.g. in `latest-report.json`).
 */
export const TECHNICAL_DEBT_INFRASTRUCTURE_CATEGORY = 'DEBT-INFRASTRUCTURE' as const;

/**
 * User-facing labels for `metrics` keys on items with {@link TECHNICAL_DEBT_INFRASTRUCTURE_CATEGORY}
 * (Mint / infrastructure pipeline). Kept next to the category id for consistency with other debt constants.
 */
export const TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_LABELS: Record<string, string> = {
  service_name: 'Service',
  env: 'Environment',
  cluster_type: 'Cluster type',
  mint_category: 'Mint category',
  mint_priority: 'Mint priority',
  rule_id: 'Rule ID',
  failed_count: 'Failed checks',
  total_checks: 'Total checks',
  is_multi: 'Multi-cluster',
  reference_link: 'Reference',
};

/** Primary grid order for infrastructure `metrics` on the product detail card. */
export const TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_ORDERED_KEYS = [
  'service_name',
  'env',
  'cluster_type',
  'mint_category',
  'mint_priority',
  'rule_id',
  'is_multi',
] as const;

/** Well-known keys outside the ordered grid (check counts + reference). */
export const TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_SUPPLEMENTAL_KEYS = [
  'failed_count',
  'total_checks',
  'reference_link',
] as const;

/**
 * Solution status constants
 */
export const SOLUTION_STATUS = {
  PROPOSED: 'PROPOSED',
  APPROVED: 'APPROVED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
} as const;

/**
 * Time period constants for trends
 */
export const TIME_PERIODS = {
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
} as const;

/**
 * Priority level constants
 */
export const PRIORITY_LEVELS = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const;

/**
 * Theme-aware color mappings for severity levels
 * Note: For chart colors, use SEVERITY_COLORS from technicalDebtColors.ts
 */
export const SEVERITY_COLORS_THEME = {
  CRITICAL: {
    light: '#d32f2f',
    dark: '#f44336',
    chip: 'secondary',
  },
  HIGH: {
    light: '#f57c00',
    dark: '#ff9800',
    chip: 'secondary',
  },
  MEDIUM: {
    light: '#1976d2',
    dark: '#2196f3',
    chip: 'primary',
  },
  LOW: {
    light: '#616161',
    dark: '#9e9e9e',
    chip: 'default',
  },
} as const;

/**
 * Theme-aware color mappings for status levels
 * Note: For chart colors, use SOLUTION_STATUS_COLORS from technicalDebtColors.ts
 */
export const STATUS_COLORS_THEME = {
  OPEN: {
    light: '#1976d2',
    dark: '#2196f3',
    background: 'rgba(25, 118, 210, 0.1)',
  },
  IN_PROGRESS: {
    light: '#f57c00',
    dark: '#ff9800',
    background: 'rgba(245, 124, 0, 0.1)',
  },
  RESOLVED: {
    light: '#388e3c',
    dark: '#4caf50',
    background: 'rgba(56, 142, 60, 0.1)',
  },
  DEFERRED: {
    light: '#616161',
    dark: '#9e9e9e',
    background: 'rgba(97, 97, 97, 0.1)',
  },
} as const;

/**
 * Icon mappings for severity levels
 */
export const SEVERITY_ICONS = {
  CRITICAL: <ErrorIcon />,
  HIGH: <WarningIcon />,
  MEDIUM: <InfoIcon />,
  LOW: <InfoIcon />,
} as const;

/**
 * Icon mappings for status levels
 */
export const STATUS_ICONS = {
  OPEN: <ErrorIcon />,
  IN_PROGRESS: <WarningIcon />,
  RESOLVED: <CheckCircleIcon />,
  DEFERRED: <InfoIcon />,
} as const;

/**
 * Labels for display
 */
export const LABELS = {
  SEVERITY: {
    CRITICAL: 'Critical',
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
  },
  STATUS: {
    OPEN: 'Open',
    IN_PROGRESS: 'In Progress',
    RESOLVED: 'Resolved',
    DEFERRED: 'Deferred',
  },
  SOLUTION_STATUS: {
    PROPOSED: 'Proposed',
    APPROVED: 'Approved',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    REJECTED: 'Rejected',
  },
  TIME_PERIOD: {
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
  },
  PRIORITY: {
    HIGH: 'High Priority',
    MEDIUM: 'Medium Priority',
    LOW: 'Low Priority',
  },
} as const;

/**
 * Helper function to get severity color based on theme
 * @param severity - Severity level
 * @param isDarkTheme - Whether dark theme is active
 * @returns Color string
 */
export const getSeverityColorTheme = (severity: string, isDarkTheme: boolean): string => {
  const colors = SEVERITY_COLORS_THEME[severity as keyof typeof SEVERITY_COLORS_THEME];
  if (!colors) return isDarkTheme ? '#9e9e9e' : '#616161';
  return isDarkTheme ? colors.dark : colors.light;
};

/**
 * Helper function to get status color based on theme
 * @param status - Status value
 * @param isDarkTheme - Whether dark theme is active
 * @returns Color string
 */
export const getStatusColorTheme = (status: string, isDarkTheme: boolean): string => {
  const colors = STATUS_COLORS_THEME[status as keyof typeof STATUS_COLORS_THEME];
  if (!colors) return isDarkTheme ? '#9e9e9e' : '#616161';
  return isDarkTheme ? colors.dark : colors.light;
};

/**
 * Helper function to get chip color variant
 * @param severity - Severity level
 * @returns Chip color variant
 */
export const getSeverityChipColor = (severity: string): 'default' | 'primary' | 'secondary' => {
  const colors = SEVERITY_COLORS_THEME[severity as keyof typeof SEVERITY_COLORS_THEME];
  if (!colors) return 'default';
  return colors.chip as 'default' | 'primary' | 'secondary';
};

/**
 * Helper function to get priority badge styles
 * @param priority - Priority level
 * @param isDarkTheme - Whether dark theme is active
 * @returns Style object for priority badge
 */
export const getPriorityBadgeStyle = (priority: string, isDarkTheme: boolean) => {
  switch (priority) {
    case 'HIGH':
      return {
        backgroundColor: isDarkTheme ? 'rgba(244, 67, 54, 0.2)' : 'rgba(211, 47, 47, 0.1)',
        color: isDarkTheme ? '#f44336' : '#d32f2f',
        border: `1px solid ${isDarkTheme ? '#f44336' : '#d32f2f'}`,
      };
    case 'MEDIUM':
      return {
        backgroundColor: isDarkTheme ? 'rgba(255, 152, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
        color: isDarkTheme ? '#ff9800' : '#f57c00',
        border: `1px solid ${isDarkTheme ? '#ff9800' : '#f57c00'}`,
      };
    case 'LOW':
      return {
        backgroundColor: isDarkTheme ? 'rgba(158, 158, 158, 0.2)' : 'rgba(97, 97, 97, 0.1)',
        color: isDarkTheme ? '#9e9e9e' : '#616161',
        border: `1px solid ${isDarkTheme ? '#9e9e9e' : '#616161'}`,
      };
    default:
      return {
        backgroundColor: isDarkTheme ? 'rgba(158, 158, 158, 0.2)' : 'rgba(97, 97, 97, 0.1)',
        color: isDarkTheme ? '#9e9e9e' : '#616161',
        border: `1px solid ${isDarkTheme ? '#9e9e9e' : '#616161'}`,
      };
  }
};
